/**
 * useHelpSearch — live help-centre results for the Cmd-K palette's Help tab.
 *
 * The counterpart to `useCommandRegistry`: that hook is pure client compute
 * over a fixed list of destinations, this one is the network half that searches
 * the actual help-centre CONTENT. Kept separate rather than folded in, because
 * the registry deliberately has no TanStack Query / no network — it has to
 * paint on the first keystroke.
 *
 * Results are mapped to {@link CommandEntry} so the Help group renders through
 * the same `StaticResultGroup` as every other command row — one row component,
 * one selection contract, one set of keyboard semantics.
 */

import { useMemo } from 'react';
import { materialGlyph } from '../../theme/glyph';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import {
  helpService,
  type HelpSearchPayload,
  type HelpSearchDegradedReason,
} from '../../services/help.service';
import { useDebounce } from '../ui/useDebounce';
import type { CommandEntry } from '../../constants/commandRegistry';

/**
 * The help centre is a public, shared corpus — the same query returns the same
 * articles for every user, PHI workspaces included — so the key is deliberately
 * NOT workspace-scoped.
 */
export const helpSearchQueryKey = (query: string) => ['help-search', query] as const;

/** Matches the palette's own search debounce so both fire on the same rhythm. */
const DEBOUNCE_MS = 200;

/**
 * The index changes on a docs deploy, so cached results stay good far longer
 * than workspace content. The backend caches for an hour too; this just stops
 * the round-trip.
 */
const STALE_TIME_MS = 10 * 60 * 1_000;

/** Mirrors the backend's RETRY_INTERVAL_MS so a degrade recovers on the same clock. */
const FAILURE_STALE_TIME_MS = 60 * 1_000;

export interface HelpSearchState {
  /** Live articles as palette rows, best-first. Empty while loading or degraded. */
  readonly entries: readonly CommandEntry[];
  /** True when the live index could not be consulted — caller should fall back. */
  readonly degraded: boolean;
  /** Why, when degraded — lets the caller distinguish a PHI block from an outage. */
  readonly reason?: HelpSearchDegradedReason;
  readonly isFetching: boolean;
}

/**
 * Map one article to a palette row.
 *
 * `id` is the URL: stable, unique, and already the natural identity of a help
 * article — which matters because cmdk's controlled highlight is keyed on it.
 * Opens in a new tab for the same reason the curated entries do: the help
 * centre is a different site, and swapping the app out from under someone
 * mid-task is not what a search result should do.
 */
function toCommandEntry(result: { title: string; url: string; snippet: string }): CommandEntry {
  return {
    id: result.url,
    label: result.title,
    description: result.snippet || undefined,
    keywords: [],
    glyph: materialGlyph('menu_book'),
    group: 'help',
    visible: () => true,
    perform: () => window.open(result.url, '_blank', 'noopener,noreferrer'),
  };
}

export function useHelpSearch(query: string, options?: { enabled?: boolean }): HelpSearchState {
  const debouncedQuery = useDebounce(query.trim(), DEBOUNCE_MS);
  const enabled = options?.enabled !== false && debouncedQuery.length > 0;

  const { data, isFetching, isPlaceholderData } = useQuery<HelpSearchPayload>({
    queryKey: helpSearchQueryKey(debouncedQuery),
    queryFn: () => helpService.search(debouncedQuery),
    enabled,
    // A degraded payload is a cached FAILURE wearing a success's clothes (the
    // service resolves rather than throws), so the success window would pin a
    // two-second blip for ten minutes — and silently override the backend's
    // deliberate 60s failure TTL by a factor of ten.
    staleTime: (query) => (query.state.data?.degraded ? FAILURE_STALE_TIME_MS : STALE_TIME_MS),
    placeholderData: keepPreviousData,
    // helpService.search resolves to a degraded payload instead of throwing, so
    // there is nothing for TanStack to retry ON — this is belt-and-braces, not
    // the load-bearing control. The transport retry that WOULD have doubled the
    // wait lives in apiService and is disabled at that layer (see help.service).
    retry: false,
  });

  // `keepPreviousData` is what stops the group flickering between corpora on
  // every keystroke — but it also keeps serving the PREVIOUS query's articles
  // whenever the current one can't be served, including forever once the box is
  // emptied (`enabled` goes false and the placeholder is all that is left).
  // Gate the ROWS on it while keeping the cache benefit.
  const stale = isPlaceholderData || !enabled;

  const entries = useMemo(
    () => (stale ? [] : (data?.results ?? []).map(toCommandEntry)),
    [stale, data?.results],
  );

  return {
    entries,
    // No data yet is not "degraded" — it's pending. Only an explicit degraded
    // payload means the live index is unavailable and the caller should show
    // the curated links instead.
    degraded: !stale && data?.degraded === true,
    reason: stale ? undefined : data?.reason,
    isFetching,
  };
}
